import React, { useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  useUpdateVenueAdminMutation,
  useVenueForManagementLazyQuery,
  useVenuesQuery,
} from '../../generated/graphql';

const isLikelyUrl = (value: string) => /^https?:\/\//i.test(value.trim());

const fieldCaptionSx = {
  display: 'block',
  mt: 1,
  color: 'text.secondary',
} as const;

type VenueOption = { id: string; name: string };

export const VenueManagementManager = () => {
  const [selectedVenueId, setSelectedVenueId] = useState('');
  const [isTest, setIsTest] = useState(false);
  const [stripeCouponId, setStripeCouponId] = useState('');
  const [crmLink, setCrmLink] = useState('');
  const [initialCrmLink, setInitialCrmLink] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const { data: venuesData, loading: venuesLoading } = useVenuesQuery();
  const [loadVenue, { data: venueData, loading: venueLoading }] =
    useVenueForManagementLazyQuery();
  const [updateVenueAdmin] = useUpdateVenueAdminMutation();

  const venues: VenueOption[] = useMemo(() => {
    if (!venuesData?.venues) return [];
    const unique = new Map(
      venuesData.venues.map((venue) => [
        venue.id,
        { id: venue.id, name: venue.name },
      ]),
    );
    return [...unique.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [venuesData]);

  const selectedVenue = venues.find((v) => v.id === selectedVenueId) ?? null;
  const venueDetails = venueData?.venueForManagement;

  const handleVenueChange = (
    _: React.SyntheticEvent<Element, Event>,
    value: VenueOption | null,
  ) => {
    const newVenueId = value?.id ?? '';
    setSelectedVenueId(newVenueId);

    if (value) {
      loadVenue({
        variables: { venueId: value.id },
        onCompleted: (result) => {
          const venue = result.venueForManagement;
          if (venue) {
            setIsTest(venue.isTest);
            setStripeCouponId(venue.stripeCouponId ?? '');
            setCrmLink(venue.crmLink ?? '');
            setInitialCrmLink(venue.crmLink ?? null);
          }
        },
      });
    } else {
      setIsTest(false);
      setStripeCouponId('');
      setCrmLink('');
      setInitialCrmLink(null);
    }
  };

  const trimmedCrmLink = crmLink.trim();
  const isCrmLinkRequired = !isTest && !!initialCrmLink;
  const isCrmLinkInvalid =
    (isCrmLinkRequired && trimmedCrmLink === '') ||
    (trimmedCrmLink !== '' && !isLikelyUrl(trimmedCrmLink));

  const handleConfirm = async () => {
    if (!selectedVenueId) return;

    try {
      await updateVenueAdmin({
        variables: {
          venueId: selectedVenueId,
          input: {
            isTest,
            stripeCouponId: stripeCouponId || null,
            crmLink: trimmedCrmLink || null,
          },
        },
        toastConfig: {
          successText: 'Venue updated successfully',
          errorText: 'Failed to update venue',
        },
      });

      loadVenue({ variables: { venueId: selectedVenueId } });
      setConfirmOpen(false);
    } catch (error) {
      console.error('Failed to update venue:', error);
    }
  };

  return (
    <Box width="100%" p={3} display="flex" justifyContent="center">
      <Box width="100%" maxWidth={800}>
        <Typography variant="h4" gutterBottom>
          Venue Management
        </Typography>

        {/* Venue Selection */}
        <Card sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Select Venue
          </Typography>
          <Autocomplete<VenueOption>
            fullWidth
            value={selectedVenue}
            options={venues}
            getOptionLabel={(o) => o.name}
            loading={venuesLoading}
            onChange={handleVenueChange}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                {option.name}
              </li>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Venue"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: venuesLoading ? (
                    <CircularProgress size={20} />
                  ) : null,
                }}
              />
            )}
          />
        </Card>

        {/* Settings */}
        {selectedVenueId && !venueLoading && venueDetails && (
          <Card sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Settings
            </Typography>

            <Box sx={{ mb: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={isTest}
                    onChange={(e) => setIsTest(e.target.checked)}
                  />
                }
                label="Test Venue"
              />
            </Box>

            <Box sx={{ mb: 3 }}>
              <TextField
                fullWidth
                label="Stripe Coupon ID"
                value={stripeCouponId}
                onChange={(e) => setStripeCouponId(e.target.value)}
              />
              <Typography variant="caption" sx={fieldCaptionSx}>
                Standing discount applied to new checkouts. Leave empty for no
                discount. Does not affect existing active subscriptions — to
                discount a current subscriber, apply the coupon directly in
                Stripe.
              </Typography>
            </Box>

            <Box sx={{ mb: 3 }}>
              <TextField
                fullWidth
                label="CRM Link (e.g. Pipedrive deal URL)"
                value={crmLink}
                onChange={(e) => setCrmLink(e.target.value)}
                required={isCrmLinkRequired}
                error={isCrmLinkInvalid}
                InputProps={{
                  endAdornment: isLikelyUrl(crmLink) ? (
                    <InputAdornment position="end">
                      <Tooltip title="Open in CRM">
                        <IconButton
                          edge="end"
                          onClick={() =>
                            window.open(
                              crmLink.trim(),
                              '_blank',
                              'noopener,noreferrer',
                            )
                          }
                        >
                          <OpenInNewIcon />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ) : null,
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  ...fieldCaptionSx,
                  color: isCrmLinkInvalid ? 'error.main' : 'text.secondary',
                }}
              >
                {(() => {
                  if (isTest) return 'Optional for test venues.';
                  if (isCrmLinkRequired)
                    return 'Cannot be removed once set; you can replace it with another URL.';
                  return 'Optional now, but once saved it cannot be removed.';
                })()}
              </Typography>
            </Box>

            <Button
              variant="contained"
              onClick={() => setConfirmOpen(true)}
              disabled={isCrmLinkInvalid}
              sx={{ mt: 2 }}
            >
              Save Changes
            </Button>
          </Card>
        )}

        {/* Confirmation Dialog */}
        <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
          <DialogTitle>Confirm Venue Update</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to update{' '}
              <strong>{selectedVenue?.name}</strong>?
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Test Venue:</strong> {isTest ? 'Yes' : 'No'}
              </Typography>
              <Typography variant="body2">
                <strong>Stripe Coupon ID:</strong> {stripeCouponId || 'None'}
              </Typography>
              <Typography variant="body2">
                <strong>CRM Link:</strong> {trimmedCrmLink || 'None'}
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
            <Button onClick={handleConfirm} variant="contained">
              Confirm
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};
